/**
 * Storage Context
 * Manages storage provider selection and initialization
 * Uses Svelte 5 runes for reactive state management
 */

import type { StorageProvider, StorageConfig, UserTier } from './StorageProvider';
import { createLocalDBProvider } from './LocalDBProvider';
import { createRemoteStorageProvider } from './RemoteStorageProvider';

// ============================================================================
// Storage State
// ============================================================================

let currentProvider = $state<StorageProvider | null>(null);
let isInitializing = $state(false);
let initError = $state<string | null>(null);

// ============================================================================
// Provider Selection
// ============================================================================

/**
 * Create and initialize storage provider based on config
 */
export async function initializeStorage(config: StorageConfig = { userTier: 'free' }): Promise<StorageProvider> {
  if (currentProvider?.isInitialized()) {
    console.log('Storage already initialized');
    return currentProvider;
  }

  isInitializing = true;
  initError = null;

  try {
    console.log(`Initializing storage for ${config.userTier} tier...`);

    // Select provider based on user tier
    const provider = createProvider(config);

    // Initialize the provider
    await provider.initialize();

    // Run migrations if using local storage
    if (provider.type === 'local') {
      const { runMigrations } = await import('../migrations');
      await runMigrations();

      // Save database after migrations
      const { saveDB } = await import('../db');
      await saveDB();
    }

    currentProvider = provider;
    isInitializing = false;

    console.log(`Storage initialized successfully (${provider.type})`);
    return provider;
  } catch (error) {
    isInitializing = false;
    initError = error instanceof Error ? error.message : 'Unknown error';
    console.error('Failed to initialize storage:', error);
    throw error;
  }
}

/**
 * Create storage provider instance based on config
 */
function createProvider(config: StorageConfig): StorageProvider {
  if (config.userTier === 'free') {
    return createLocalDBProvider();
  } else if (config.userTier === 'paid') {
    return createRemoteStorageProvider(config);
  } else {
    throw new Error(`Unknown user tier: ${config.userTier}`);
  }
}

/**
 * Get current storage provider
 * Throws error if not initialized
 */
export function getStorageProvider(): StorageProvider {
  if (!currentProvider) {
    throw new Error('Storage not initialized. Call initializeStorage() first.');
  }
  return currentProvider;
}

/**
 * Check if storage is initialized
 */
export function isStorageInitialized(): boolean {
  return currentProvider?.isInitialized() ?? false;
}

/**
 * Get initialization state
 */
export function getStorageState() {
  return {
    provider: currentProvider,
    isInitializing,
    error: initError,
    isInitialized: isStorageInitialized()
  };
}

/**
 * Switch storage provider (e.g., free to paid upgrade)
 * This will require data migration between providers
 */
export async function switchStorageProvider(newConfig: StorageConfig): Promise<void> {
  if (!currentProvider) {
    throw new Error('No current provider to migrate from');
  }

  console.log(`Migrating from ${currentProvider.type} to ${newConfig.userTier} tier...`);

  // TODO: Implement data migration between providers
  // 1. Export data from current provider
  // 2. Initialize new provider
  // 3. Import data to new provider
  // 4. Verify migration
  // 5. Switch providers

  throw new Error('Provider migration not yet implemented');
}

/**
 * Export storage state for debugging
 */
export function exportStorageDebugInfo() {
  return {
    providerType: currentProvider?.type,
    isInitialized: isStorageInitialized(),
    isInitializing,
    error: initError
  };
}
